/**
 * PronunciationRecorder — ghi âm câu Phát âm khi làm bài tập/bài kiểm tra
 * (plan 2026-10-03).
 *
 * Ghi âm (tối đa 60s) → upload ngay lên `/attempts/<id>/answer/` (ghi đè bản cũ) →
 * nghe lại được. CHƯA chấm — Gemini chấm sau khi nộp bài.
 */
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Space, Tag, Typography, message } from "antd";
import { AudioOutlined, CheckCircleOutlined, StopOutlined } from "@ant-design/icons";

import { fetchAnswerAudioUrl, uploadPronunciationAudio } from "@/api/assessment";
import { ListenSampleButton } from "@/components/ListenSampleButton";

const { Text } = Typography;

const MAX_SECONDS = 60;

const LANGUAGE_LABEL: Record<string, string> = {
  ja: "Tiếng Nhật",
  en: "Tiếng Anh",
  zh: "Tiếng Trung",
  ko: "Tiếng Hàn",
};

interface Props {
  attemptId: number;
  questionId: number;
  target: string;
  language?: string;
  /** Đã có bản ghi lưu trên server (reload giữa bài). */
  savedAnswerId?: number;
  hasSavedAudio?: boolean;
  onSaved: (answerId: number) => void;
  /** Bài tập → hiện "Nghe mẫu" (giọng máy trình duyệt). Bài kiểm tra → false. */
  allowSample?: boolean;
}

export function PronunciationRecorder({
  attemptId,
  questionId,
  target,
  language,
  savedAnswerId,
  hasSavedAudio = false,
  onSaved,
  allowSample = false,
}: Props) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [saved, setSaved] = useState(hasSavedAudio);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof window !== "undefined" &&
    "MediaRecorder" in window;

  useEffect(() => {
    setSaved(hasSavedAudio);
  }, [hasSavedAudio, questionId]);

  // Dọn object URL + timer khi đổi câu / unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const upload = async (blob: Blob) => {
    setUploading(true);
    try {
      const res = await uploadPronunciationAudio(attemptId, questionId, blob);
      setAudioUrl(URL.createObjectURL(blob));
      setSaved(true);
      onSaved(res.answer_id);
      message.success("Đã lưu bản ghi");
    } catch {
      message.error("Không lưu được bản ghi, hãy ghi lại.");
    } finally {
      setUploading(false);
    }
  };

  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    setRecording(false);
  };

  const start = async () => {
    if (!supported) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        void upload(new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" }));
      };
      recorderRef.current = rec;
      rec.start();
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      setRecording(false);
      message.error("Không truy cập được micro. Kiểm tra quyền trình duyệt.");
    }
  };

  // Tự dừng khi chạm giới hạn thời lượng.
  useEffect(() => {
    if (recording && seconds >= MAX_SECONDS) stop();
  }, [recording, seconds]);

  const playSaved = async () => {
    if (!savedAnswerId) return;
    try {
      setAudioUrl(await fetchAnswerAudioUrl(savedAnswerId));
    } catch {
      message.error("Không tải được bản ghi đã lưu.");
    }
  };

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }} data-testid="pron-recorder">
      <div>
        {language && <Tag color="blue">{LANGUAGE_LABEL[language] ?? language}</Tag>}
        <Text type="secondary">Đọc to câu sau:</Text>
        <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{target}</div>
        {allowSample && language && (
          <div style={{ marginTop: 8 }}>
            <ListenSampleButton text={target} language={language} size="small" />
          </div>
        )}
      </div>

      {!supported ? (
        <Alert type="warning" showIcon message="Trình duyệt không hỗ trợ ghi âm." />
      ) : (
        <Space wrap>
          {recording ? (
            <Button danger icon={<StopOutlined />} onClick={stop} data-testid="pron-stop">
              Dừng ({seconds}s / {MAX_SECONDS}s)
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<AudioOutlined />}
              loading={uploading}
              onClick={start}
              data-testid="pron-record"
            >
              {uploading ? "Đang lưu…" : saved ? "Ghi lại" : "Ghi âm"}
            </Button>
          )}
          {saved && !recording && (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Đã lưu bản ghi
            </Tag>
          )}
          {saved && !audioUrl && savedAnswerId && (
            <Button size="small" onClick={playSaved}>
              Nghe bản đã lưu
            </Button>
          )}
        </Space>
      )}

      {audioUrl && <audio controls src={audioUrl} style={{ width: "100%" }} />}

      <Alert
        type="info"
        showIcon
        message="Câu này được AI chấm phát âm sau khi bạn nộp bài. Bạn có thể ghi lại nhiều lần — hệ thống dùng bản ghi cuối cùng."
      />
    </Space>
  );
}
